import * as React from 'react';
import {useNavigate} from 'react-router-dom';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic';
import {useCreateUserPlaylist, useUserPlaylists} from '../../store/slices/userPlaylists/userPlaylistsSlice';
import {useFavorites} from '../../store/slices/favorites/favoritesSlice';
import {LIKED_SONGS_ID, LIKED_SONGS_NAME, useLikedSongs} from '../../store/slices/likedSongs/likedSongsSlice';
import {MediaCard} from '../common/mediaItem/MediaCard';
import {EmptyState} from '../common/emptyState/EmptyState';
import {CreatePlaylistDialog} from './CreatePlaylistDialog';
import {useAuth} from '../../auth/AuthContext';
import styles from './Library.module.scss';

export const Library: React.FC = () => {
    const playlists = useUserPlaylists();
    const favorites = useFavorites();
    const likedSongs = useLikedSongs();
    const createUserPlaylist = useCreateUserPlaylist();
    const navigate = useNavigate();
    const {requireAuth} = useAuth();
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const handleAddClick = () => {
        requireAuth(() => setDialogOpen(true), 'create your own playlists');
    };

    const handleCreate = (name: string) => {
        const id = createUserPlaylist(name);
        setDialogOpen(false);
        navigate(`/playlists/${id}`);
    };

    const isEmpty = playlists.length === 0 && favorites.length === 0 && likedSongs.length === 0;

    const likedCount = likedSongs.length;
    const likedCountLabel = `${likedCount} ${likedCount === 1 ? 'song' : 'songs'}`;

    return (
        <div className={styles.library}>
            <div className={styles.library__header}>
                <div className={styles.library__title}>My Library</div>
                <IconButton onClick={handleAddClick} color="secondary">
                    <AddIcon/>
                </IconButton>
            </div>

            {isEmpty ? (
                <EmptyState
                    icon={<LibraryMusicIcon/>}
                    title="No playlists yet"
                    description="Tap + to create one, or tap the heart on any song to build your Liked Songs."
                />
            ) : (
                <>
                    {likedCount > 0 && (
                        <div
                            className={styles.library__likedCard}
                            onClick={() => navigate(`/playlists/${LIKED_SONGS_ID}`)}
                            role="button"
                            tabIndex={0}
                            aria-label="Open Liked Songs"
                        >
                            <div className={styles.library__likedArt}>
                                <FavoriteIcon className={styles.library__likedIcon}/>
                            </div>
                            <div className={styles.library__likedText}>
                                <div className={styles.library__likedTitle}>{LIKED_SONGS_NAME}</div>
                                <div className={styles.library__likedCount}>{likedCountLabel}</div>
                            </div>
                        </div>
                    )}

                    {playlists.length > 0 && (
                        <div className={styles.library__section}>
                            <div className={styles.library__sectionTitle}>My Playlists</div>
                            <div className={styles.library__grid}>
                                {playlists.map(pl => (
                                    <MediaCard
                                        key={pl.id}
                                        variant="horizontal"
                                        item={{id: pl.id, name: pl.name, artist: `${pl.tracks.length} songs`, img: pl.img}}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {favorites.length > 0 && (
                        <div className={styles.library__section}>
                            <div className={styles.library__sectionTitle}>Favorites</div>
                            <div className={styles.library__grid}>
                                {favorites.map(pl => (
                                    <MediaCard
                                        key={pl.id}
                                        variant="horizontal"
                                        item={{id: pl.id, name: pl.name, artist: pl.artist, img: pl.img}}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}

            <CreatePlaylistDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={handleCreate}
            />
        </div>
    );
};
